import { Box, Stack } from '@mui/material'
import { Context, useEffect } from 'react'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import { CenteredSpinner } from 'src/components/Spinner'
import { MainComponentStatus } from 'src/enums'
import { IEmptyStatusProps } from 'src/generated-types/empty-status'
import { MooringDropdownDto } from 'src/generated-types/mooring-drop-down-dto'
import { IGetDocumentsParams } from '../../../../api/document'
import { useGetDocumentCategoriesWithDocuments } from '../../../../api/users/document-category'
import NoDocument from '../../../../assets/svg/no-documents.svg'
import { DocumentsTableByDocumentCategory } from './DocumentsTableByDocumentCategory'

export interface IAccordionDocumentsTableProps extends IEmptyStatusProps {
  customerId: EntityId
  localityId: EntityId
  documentTypeId: EntityId
  documentsParams: IGetDocumentsParams
  height?: number | string
  context?: React.Context<any>
  moorings?: MooringDropdownDto[]
  equipmentStatus?: MainComponentStatus
  hideMooringColumn?: boolean
  spinnerHeight?: number
  hideColumns?: string[]
  additionalComponent?: React.ReactNode
  removeBoxShadow?: boolean
  tableRowHeight?: number
  actionButtonheightAndWidth?: string
  iconWidth?: string
  accordionBackgroundColor?: string
  accordionBorderStyle?: string
  expandedBottomMargin?: string
  hideEmptyState?: boolean
  setIsTypesLoading?: React.Dispatch<React.SetStateAction<boolean>>
  autoHeight?: boolean
  maximumContentHeight?: number
  minimalEmptyStatus?: boolean
  setDataLength?: (length: number) => void
  disabled?: boolean
  containerRef?: React.Ref<HTMLDivElement>
}

export const AccordionDocumentsTable: React.FC<IAccordionDocumentsTableProps> = ({
  customerId,
  localityId,
  documentsParams,
  height,
  context,
  moorings,
  emptyStatusImageWidth,
  emptyStatusTitle,
  emptyStatusContent,
  emptyStatusHeight,
  emptyStatusContentClass,
  emptyStatusImage,
  emptyStatusContentFontSize,
  emptyStatusTitleFontSize,
  imageContainerClasses,
  contentTopMargin,
  equipmentStatus,
  hideMooringColumn,
  spinnerHeight,
  hideColumns,
  additionalComponent,
  removeBoxShadow,
  tableRowHeight,
  actionButtonheightAndWidth,
  iconWidth,
  accordionBackgroundColor,
  accordionBorderStyle,
  expandedBottomMargin,
  hideEmptyState = false,
  setIsTypesLoading,
  autoHeight = false,
  maximumContentHeight,
  minimalEmptyStatus = false,
  setDataLength,
  disabled = false,
  containerRef,
}) => {
  const { data: documentTypes, isLoading } = useGetDocumentCategoriesWithDocuments(customerId, { ...documentsParams })

  useEffect(() => {
    if (setIsTypesLoading) {
      setIsTypesLoading(isLoading)
    }
  }, [isLoading, setIsTypesLoading])

  useEffect(() => {
    setDataLength && setDataLength(documentTypes?.length ?? 0)
  }, [documentTypes, setDataLength])

  return isLoading ? (
    <CenteredSpinner height={spinnerHeight} />
  ) : (
    <>
      <Stack
        ref={containerRef}
        spacing={1}
        className="scrollbar"
        height={height}
        maxHeight={maximumContentHeight ?? undefined}
        sx={{ overflowY: 'auto' }}
        paddingRight={'2px'}
      >
        {(documentTypes && documentTypes.length > 0) || additionalComponent != null ? (
          <>
            {documentTypes?.map(documentCategory => (
              <DocumentsTableByDocumentCategory
                key={documentCategory?.id}
                customerId={customerId}
                localityId={localityId}
                documentsParams={{ documentCategoryId: documentCategory?.id as EntityId, ...documentsParams }}
                showEditDocumentButton={true}
                showDocumentsHeader={false}
                documentCategoryInfo={documentCategory}
                context={context as Context<any>}
                moorings={moorings}
                equipmentStatus={equipmentStatus}
                hideMooringColumn={hideMooringColumn}
                hideColumns={hideColumns}
                removeBoxShadow={removeBoxShadow}
                tableRowHeight={tableRowHeight}
                actionButtonheightAndWidth={actionButtonheightAndWidth}
                iconWidth={iconWidth}
                accordionBackgroundColor={accordionBackgroundColor}
                accordionBorderStyle={accordionBorderStyle}
                expandedBottomMargin={expandedBottomMargin}
                autoHeight={autoHeight}
                disabled={disabled}
              />
            ))}
            {additionalComponent}
          </>
        ) : hideEmptyState ? null : (
          <Box py={2} className="text-center" height="100%">
            <EmptyStatusCard
              image={emptyStatusImage ?? NoDocument}
              header={emptyStatusTitle}
              content={emptyStatusContent}
              imageWidth={emptyStatusImageWidth ?? '162px'}
              contentClasses={emptyStatusContentClass}
              height={emptyStatusHeight}
              contentTopMargin={contentTopMargin ?? '16px'}
              imageContainerClasses={imageContainerClasses ?? 'pb-5'}
              contentFontSize={emptyStatusContentFontSize}
              headerFontSize={emptyStatusTitleFontSize}
              hideImage={minimalEmptyStatus}
              compact={minimalEmptyStatus}
            />
          </Box>
        )}
      </Stack>
    </>
  )
}
